import { IconStarFilled, IconGrip } from '@/components/ui/Icon';
import { useDragReorder } from '@/shared/hooks/useDragReorder';
import { useFavoritesStore } from '@/features/favorites/useFavoritesStore';
import { favoriteHref } from '@/features/favorites/resolveFavoriteContext';
import { favoriteIcon } from '@/features/favorites/entityIcons';
import { useHomeNav } from '../useHomeNav';
import { registerWidget } from '../registry';

// Repurposed from the old "Shortcuts" catalog into a tile grid of the user's
// favorited PAGES (starred records live in the "Follow up" widget instead, so
// they're filtered out here). The widget type stays 'shortcuts' so any saved
// layout that already references it keeps working.
function FavoritesTilesRender() {
  const nav = useHomeNav();
  const favorites = useFavoritesStore((s) => s.favorites);
  const pages = favorites.filter((f) => f.kind !== 'record');
  if (pages.length === 0) {
    return <div className="text-sm text-muted">Star a page to see it here.</div>;
  }
  return (
    <div className="grid grid-cols-3 gap-2">
      {pages.map((f) => (
        <button key={f.id} onClick={() => nav(favoriteHref(f), false)} title={f.label}
          className="flex flex-col items-center gap-1 rounded-lg border border-primary/25 bg-primary-light p-2 hover:border-primary/50 transition">
          <span className="text-primary shrink-0">{favoriteIcon(f, 16)}</span>
          <span className="w-full text-[0.6875rem] text-primary-text text-center leading-tight truncate">{f.label.split('·').pop()?.trim() || f.label}</span>
        </button>
      ))}
    </div>
  );
}

// Configure = rearrange/remove the starred pages behind the tiles. Reordering
// sends just the page ids — the backend updates only those, so the Follow up
// widget's record order is untouched.
function FavoritesTilesConfig() {
  const favorites = useFavoritesStore((s) => s.favorites);
  const remove = useFavoritesStore((s) => s.remove);
  const reorder = useFavoritesStore((s) => s.reorder);
  const pages = favorites.filter((f) => f.kind !== 'record');
  const { handleProps, rowProps } = useDragReorder(pages, (next) => reorder(next.map((f) => f.id)));
  if (pages.length === 0) {
    return <div className="text-[0.75rem] text-muted">No starred pages yet. Star a page to see it here as a tile.</div>;
  }
  return (
    <div className="flex flex-col gap-1">
      <div className="text-meta text-muted">Drag to rearrange the tiles; changes apply immediately.</div>
      <div className="max-h-56 overflow-auto rounded-md border border-border-light">
        {pages.map((f, i) => {
          const rp = rowProps(i);
          return (
            <div key={f.id} onDragOver={rp.onDragOver} onDrop={rp.onDrop}
              className={`flex items-center gap-2 px-2 py-1 text-[0.8125rem] ${rp.isOver ? 'ring-2 ring-primary' : ''}`}>
              <span {...handleProps(i)} className="text-muted cursor-grab select-none" title="Drag to reorder"><IconGrip size={13} /></span>
              <span className="shrink-0 text-secondary">{favoriteIcon(f, 14)}</span>
              <span className="flex-1 min-w-0 truncate" title={f.label}>{f.label}</span>
              <button type="button" className="text-muted hover:text-rose-500 text-[0.75rem]" onClick={() => void remove(f.id)}>Remove</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

registerWidget({
  type: 'shortcuts',
  label: 'Favorites',
  defaultSize: 'M',
  icon: <IconStarFilled size={13} />,
  accent: '#f59e0b',
  ConfigForm: FavoritesTilesConfig,
  Render: FavoritesTilesRender,
});
